import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

/**
 * A right-click context menu: portaled to the body, positioned at the cursor
 * and clamped inside the viewport. Dismisses on outside press, Escape, scroll,
 * or resize. Items run their handler and auto-close.
 *
 * items: Array<{ label, icon?, onClick, danger?, disabled? } | { separator: true }>
 */
export default function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Clamp into the viewport once the menu has a measurable size.
  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - rect.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - rect.height - 8)),
    });
  }, [x, y]);

  useLayoutEffect(() => {
    const onPress = (e) => {
      if (!ref.current?.contains(e.target)) onClose?.();
    };
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    const dismiss = () => onClose?.();
    document.addEventListener('pointerdown', onPress);
    document.addEventListener('keydown', onKey);
    // Capture phase so scrolls inside nested containers dismiss too.
    document.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', onPress);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ left: pos.left, top: pos.top }}
      className="fixed z-50 w-52 rounded-xl border border-slate-200 bg-slate-100 p-1.5 shadow-lg animate-scale-in"
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {items
        .filter(Boolean)
        .map((item, index) =>
          item.separator ? (
            <div key={`sep-${index}`} className="my-1 h-px bg-slate-200" role="separator" />
          ) : (
            <button
              key={item.label}
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                item.onClick?.();
                onClose?.();
              }}
              className={clsx(
                'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                item.danger
                  ? 'text-red-500 hover:bg-red-50'
                  : 'text-slate-700 hover:bg-slate-200',
              )}
            >
              {item.icon && <item.icon className="h-4 w-4 shrink-0 text-current" />}
              <span className="flex-1 truncate text-left">{item.label}</span>
            </button>
          ),
        )}
    </div>,
    document.body,
  );
}
